import { useMemo, useEffect, useRef } from "react";
import axios from 'axios';
import useAuthProvider from "./useAuthProvider";
import { API_HOST } from "configs/api";
import { PAGE_NAV } from "configs/page";

const useCreateFetchProvider = () => {
    return useMemo(() => {
        return axios.create({
            baseURL: API_HOST,
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
        });
    }, []);
};

const useFetchProvider = () => {
    const authProvider = useAuthProvider();
    const fetchAuth = useCreateFetchProvider();

    // Keep a stable ref to the latest authProvider so the interceptors
    // (registered once) always call current functions without re-registration.
    const authRef = useRef(authProvider);
    authRef.current = authProvider;

    useEffect(() => {
        const requestId = fetchAuth.interceptors.request.use(
            config => {
                config.headers.Authorization = `Bearer ${authRef.current.getBearerToken()}`;
                return config;
            },
            error => Promise.reject(error)
        );

        const responseId = fetchAuth.interceptors.response.use(
            response => response,
            async error => {
                if (error.response) {
                    if (error.response.status === 403) return Promise.reject(error);

                    const config = error.config;
                    if (config.maxRetries === undefined) config.maxRetries = 3;
                    config.__retryCount = config.__retryCount || 0;

                    if (config.__retryCount < config.maxRetries) {
                        config.__retryCount += 1;

                        if (error.response.status === 401 && !config._retry) {
                            config._retry = true;

                            return authRef.current.getNewAccessToken(config)
                                .then(
                                    async () => {
                                        await new Promise(resolve => setTimeout(resolve, 300));
                                        return fetchAuth(config);
                                    },
                                    () => {
                                        authRef.current.clearTokens();
                                        window.location.href = PAGE_NAV.LOGIN;
                                        return Promise.reject(error);
                                    }
                                );
                        }

                        await new Promise(resolve => setTimeout(resolve, 300));
                        return fetchAuth(config);
                    }

                    return Promise.reject(error);
                }

                // Network error or no response — do not clear the session;
                // a transient outage should not log the user out.
                return Promise.reject(error);
            }
        );

        return () => {
            fetchAuth.interceptors.request.eject(requestId);
            fetchAuth.interceptors.response.eject(responseId);
        };
    }, [fetchAuth]); // fetchAuth is stable — interceptors register exactly once per instance

    return fetchAuth;
};

export default useFetchProvider;
